import * as Sentry from "@sentry/react";
import React, { useEffect } from "react";
import { MapProvider } from "react-map-gl/mapbox";
import "../../packs/App.css";
import CommandError from "../components/CommandCenter/CommandError";
import { fetchInitialData } from "../components/PlotAPI/CommandCenter";
import ProjectBanner from "../components/Project/ProjectBanner";
import Helper from "../Helper";
import { Mixpanel } from "../Mixpanel";
import { CommandCenterView } from "../models/CommandCenterView";
import ColumnContainer from "./ColumnContainer";
import CommandCenterHeader from "./CommandCenterHeader";
import CommandCenterQRCodeCard from "./CommandCenterQRCodeCard";
import { CommandMessage, CommandProjectInfo } from "./CommandTypes";
import DashboardJobsiteAlerts from "./DashboardAlerts/DashboardJobsiteAlerts";
import FullscreenJobsiteAlert from "./FullscreenJobsiteAlert";
import { JobsiteCommandContext } from "./JobsiteCommandProvider";
import LoadingSplash from "./LoadingSplash";
import ProgressBar from "./ProgressBar";

interface DashboardProps {
    commandCenterViewProp?: CommandCenterView;
}

// eslint-disable-next-line complexity
const Dashboard: React.FC<DashboardProps> = ({ commandCenterViewProp }) => {
    const searchParams = commandCenterViewProp
        ? commandCenterViewProp.toUrlSearchParams()
        : new URLSearchParams(window.location.search);
    const commandContext = React.useContext(JobsiteCommandContext);

    const [projectInfo, setProjectInfo] = React.useState<CommandProjectInfo | undefined>(undefined);
    const [commandCenterView, setCommandCenterView] = React.useState<CommandCenterView | undefined>(undefined);
    const [messages, setMessages] = React.useState<CommandMessage[]>([]);
    const [commandCable, setCommandCable] = React.useState<any>(null);

    useEffect(() => {
        // Fetch project info from the API
        if (!projectInfo && !commandCenterView) {
            reloadData();
        }
        if (!commandCenterView?.id || !projectInfo?.id) {
            return;
        }
        // Assume these are public devices
        Mixpanel.guest_identify();
        Mixpanel.track("Loaded Jobsite Command", {
            "Command Center View": commandCenterView.id || "Unknown",
            Project: projectInfo.id || "Unknown",
        });
    }, [projectInfo, commandCenterView]);

    const reloadData = () => {
        fetchInitialData(searchParams).then((data) => {
            setCommandData(data);
        });
    };

    const setCommandData = (data: any) => {
        setProjectInfo(data.project.project_info);
        setCommandCenterView(data.commandView as CommandCenterView);
        setMessages(data.messages as CommandMessage[]);
        commandContext?.setLastUpdated(new Date());
        commandContext?.setError(null);
    };

    const reloadDataInterval = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        reloadDataInterval.current = setInterval(
            () => {
                reloadData();
            },
            60 * 60 * 1000 // Update every 60 minutes
        );

        return () => {
            if (reloadDataInterval.current) {
                clearInterval(reloadDataInterval.current);
            }
        };
    }, []);

    if (commandContext?.error) {
        return (
            <div className="relative w-screen h-screen flex items-center justify-center bg-plot-slate">
                <CommandError image={"/img/png/logo/plot_jobsite_command.png"} error={commandContext?.error} />
            </div>
        );
    }
    useEffect(() => {
        const body = document.querySelector("body");
        body?.setAttribute("data-theme", "dark");
        body?.setAttribute("data-color-scheme", "dark");

        document.documentElement.classList.add("dark");
    }, []);

    useEffect(() => {
        if (commandContext?.cableApp && commandCenterView) {
            createCommandChannel();
        }
    }, [commandCenterView, commandContext?.cableApp]);

    const createCommandChannel = () => {
        if (commandCable) {
            try {
                commandCable.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
            setCommandCable(null);
        }

        const newCommandCable = commandContext?.cableApp?.subscribeTo("DashboardRefreshChannel", {
            command_center_view_id: commandCenterView?.id,
        });

        newCommandCable?.on("message", (data: any) => {
            if (data.refresh) {
                reloadData();
            }
        });

        newCommandCable?.on("disconnect", (data) => {
            setCommandCable(null);
        });

        setCommandCable(newCommandCable || null);
    };

    return (
        <>
            <div className="relative w-full h-full flex items-center justify-center bg-plot-slate">
                {!commandCenterViewProp && (
                    <LoadingSplash
                        commandCenterView={commandCenterView}
                        image={"/img/png/logo/plot_jobsite_command.png"}
                        teamLogo={projectInfo?.avatar_url || undefined}
                    />
                )}
                {projectInfo && (
                    <>
                        <MapProvider>
                            <div
                                className="h-full w-full flex flex-col overflow-hidden relative bg-plot-slate"
                                style={{ zoom: commandContext?.zoom ? commandContext.zoom : 1 }}
                            >
                                <CommandCenterHeader projectInfo={projectInfo} commandCenterView={commandCenterView} />

                                <div
                                    className={Helper.classNames(
                                        "relative flex h-full py-2 pb-4 px-8 gap-8 w-full overflow-hidden",
                                        commandContext?.isPortrait ? "flex-col" : "flex-row"
                                    )}
                                >
                                    <FullscreenJobsiteAlert project={projectInfo} messages={messages} />
                                    <ColumnContainer
                                        projectInfo={projectInfo}
                                        commandCenterSection={commandCenterView?.commandCenterSections?.find(
                                            (view) => view.sectionType === "left_column"
                                        )}
                                    />
                                    <ColumnContainer
                                        projectInfo={projectInfo}
                                        commandCenterSection={commandCenterView?.commandCenterSections?.find(
                                            (view) => view.sectionType === "center_column"
                                        )}
                                    />

                                    {commandContext?.isPortrait && <ProjectBanner projectInfo={projectInfo} />}

                                    <div
                                        className={Helper.classNames(
                                            "flex items-stretch gap-4",
                                            commandContext?.isPortrait
                                                ? "w-full flex-row max-h-[20vh] pb-2"
                                                : "flex-col flex-0"
                                        )}
                                    >
                                        <DashboardJobsiteAlerts messages={messages} timezone={projectInfo.timezone} />
                                        {!commandContext?.isPortrait && <ProjectBanner projectInfo={projectInfo} />}

                                        <CommandCenterQRCodeCard
                                            url={`${Helper.baseUrlAltSubdomain(projectInfo?.subdomain || "app")}/projects/${projectInfo?.unique_token}/portal?book=true`}
                                            projectInfo={projectInfo}
                                        />
                                    </div>
                                </div>
                            </div>
                        </MapProvider>
                        <ProgressBar
                            interval={projectInfo?.jobsite_command_interval || 15}
                            intervalUpdate={commandContext?.intervalUpdate || false}
                            setIntervalUpdate={commandContext?.setIntervalUpdate || (() => {})}
                            runInterval={commandContext?.runInterval || false}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default Dashboard;
